import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { Bell, Plus, Trash2, Mail, MessageSquare, Webhook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

interface Project {
  id: string;
  name: string;
}

interface Alert {
  id: string;
  name: string;
  condition: string;
  threshold: number;
  channel: 'email' | 'slack' | 'webhook';
  enabled: boolean;
}

function AlertsPage() {
  const { accessToken } = useAuth();
  const [projectId, setProjectId] = useState('');
  const [alerts, setAlerts] = useState<Alert[]>([
    {
      id: '1',
      name: 'High Error Rate',
      condition: 'error_rate',
      threshold: 5,
      channel: 'email',
      enabled: true,
    },
    {
      id: '2',
      name: 'Service Down',
      condition: 'no_logs',
      threshold: 300,
      channel: 'slack',
      enabled: true,
    },
    {
      id: '3',
      name: 'Spike Detection',
      condition: 'volume_spike',
      threshold: 200,
      channel: 'webhook',
      enabled: false,
    },
  ]);

  const { data: projectsData } = useQuery<{ projects: Project[] }>({
    queryKey: ['projects'],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      const response = await fetch(`${API_URL}/v1/user/projects`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch projects');
      return response.json();
    },
    enabled: !!accessToken,
  });

  const projects = projectsData?.projects || [];

  useEffect(() => {
    if (projects.length > 0 && !projectId) {
      setProjectId(projects[0].id);
    }
  }, [projects, projectId]);

  const toggleAlert = (id: string) => {
    setAlerts((prev) =>
      prev.map((alert) => (alert.id === id ? { ...alert, enabled: !alert.enabled } : alert))
    );
  };

  const deleteAlert = (id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'slack':
        return <MessageSquare className="h-4 w-4" />;
      case 'webhook':
        return <Webhook className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getConditionLabel = (condition: string) => {
    switch (condition) {
      case 'error_rate':
        return 'Error Rate';
      case 'no_logs':
        return 'No Logs';
      case 'volume_spike':
        return 'Volume Spike';
      default:
        return condition;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Alerts</h1>
          <p className="text-muted-foreground">Configure alerts for your logging events</p>
        </div>
        <div className="flex gap-3">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Alert
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Alerts
            </CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {alerts.filter((a) => a.enabled).length}
            </div>
            <p className="text-xs text-muted-foreground">of {alerts.length} total</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Alerts Triggered (24h)
            </CardTitle>
            <Bell className="h-4 w-4 text-yellow-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">12</div>
            <p className="text-xs text-muted-foreground">+3 from yesterday</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Notifications Sent
            </CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">48</div>
            <p className="text-xs text-muted-foreground">this week</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Alert Rules</CardTitle>
          <CardDescription>Manage your alert configurations</CardDescription>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Bell className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-foreground">No alerts configured</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Create your first alert to get notified about important events
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between rounded-lg border border-border p-4"
                >
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={alert.enabled}
                      onCheckedChange={() => toggleAlert(alert.id)}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-foreground">{alert.name}</h4>
                        <Badge variant={alert.enabled ? 'success' : 'secondary'}>
                          {alert.enabled ? 'Active' : 'Disabled'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {getConditionLabel(alert.condition)} &gt; {alert.threshold}
                        {alert.condition === 'error_rate' ? '%' : alert.condition === 'no_logs' ? 's' : '%'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-1.5">
                      {getChannelIcon(alert.channel)}
                      <span className="text-sm capitalize">{alert.channel}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => deleteAlert(alert.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/alerts')({
  component: AlertsPage,
});
